/**
 * Item database: names, sprites, stack sizes and the tool / armor / food / fuel
 * stats of every registered item (vanilla 1.21 numbers), plus block entries
 * (name from BLOCKS, stack 64, wood-ish fuel). DOM-free so node smoke tests,
 * workers and UI all share it.
 *
 *   itemDef(id)      full def for an item or a block id (cached, never undefined)
 *   maxStackOf(id)   64 / 16 / 1
 *   maxDamageOf(id)  durability, 0 = not damageable
 *   spriteOf(id)     vanilla `item/<name>.png` basename for items ('' for blocks)
 */

import { BLOCKS } from '../blocks';
import * as B from '../blocks';
import { SPEAR_TIERS } from './spear';
import { ALL_ITEM_IDS, I, ITEM_BASE, itemNameOf, type ItemName } from './itemIds';

export type ToolType = 'sword' | 'pickaxe' | 'axe' | 'shovel' | 'hoe' | 'shears' | 'mace' | 'trident' | 'spear';

export interface ItemDef {
  /** Shows the crosshair cooldown bar in Bedrock combat (spear jab). */
  cooldownBar?: boolean;
  id: number;
  name: string;
  maxStack: number;
  /** Durability (uses before breaking); 0 = not damageable. */
  maxDamage: number;
  tool?: {
    type: ToolType;
    /** 0 wood/gold, 1 stone, 2 iron, 3 diamond. */
    tier: number;
    /** Mining speed multiplier on matching blocks. */
    speed: number;
    /** Attack damage in half-hearts (vanilla: sword 4..7, axe 3..9, pick 2..5, ...). */
    attack: number;
    /** Attack speed (attacks/second) for the cooldown meter. */
    attackSpeed: number;
  };
  armor?: { slot: 0 | 1 | 2 | 3; defense: number; toughness: number; knockbackResistance?: number };
  food?: { hunger: number; saturation: number; alwaysEdible?: boolean };
  /** Furnace burn time in ticks (0 = not fuel). */
  fuel: number;
}

// --- tools -------------------------------------------------------------------

/** wood, stone, iron, gold, diamond (sprite/registry prefix, tier, durability, speed). */
const MATERIALS = [
  { key: 'WOODEN', tier: 0, dur: 59, speed: 2 },
  { key: 'STONE', tier: 1, dur: 131, speed: 4 },
  { key: 'IRON', tier: 2, dur: 250, speed: 6 },
  { key: 'GOLDEN', tier: 0, dur: 32, speed: 12 },
  { key: 'DIAMOND', tier: 3, dur: 1561, speed: 8 },
  { key: 'NETHERITE', tier: 4, dur: 2031, speed: 9 },
  { key: 'COPPER', tier: 1, dur: 190, speed: 5 },
] as const;
/** Total damage dealt per material: [sword, axe, pickaxe, shovel, hoe]. */
const ATTACK: Record<string, readonly [number, number, number, number, number]> = {
  WOODEN: [4, 7, 2, 2.5, 1], STONE: [5, 9, 3, 3.5, 1], IRON: [6, 9, 4, 4.5, 1],
  GOLDEN: [4, 7, 2, 2.5, 1], DIAMOND: [7, 9, 5, 5.5, 1], NETHERITE: [8, 10, 6, 6.5, 1],
  COPPER: [5, 9, 3, 3.5, 1],
};
const AXE_SPEED: Record<string, number> = { WOODEN: 0.8, STONE: 0.8, IRON: 0.9, GOLDEN: 1, DIAMOND: 1, NETHERITE: 1, COPPER: 0.8 };
const HOE_SPEED: Record<string, number> = { WOODEN: 1, STONE: 2, IRON: 3, GOLDEN: 1, DIAMOND: 4, NETHERITE: 4, COPPER: 2 };

// --- armor -------------------------------------------------------------------

const ARMOR_MATS = [
  { key: 'LEATHER', defense: [1, 3, 2, 1], mult: 5, toughness: 0 },
  { key: 'IRON', defense: [2, 6, 5, 2], mult: 15, toughness: 0 },
  { key: 'GOLDEN', defense: [2, 5, 3, 1], mult: 7, toughness: 0 },
  { key: 'DIAMOND', defense: [3, 8, 6, 3], mult: 33, toughness: 2 },
  { key: 'NETHERITE', defense: [3, 8, 6, 3], mult: 37, toughness: 3, kb: 0.1 },
  { key: 'COPPER', defense: [2, 4, 3, 1], mult: 11, toughness: 0 },
] as const;
const ARMOR_PIECES = ['HELMET', 'CHESTPLATE', 'LEGGINGS', 'BOOTS'] as const;
const ARMOR_BASE_DUR = [11, 16, 15, 13];

// --- food: [hunger, saturation, alwaysEdible?] ---------------------------------

const FOOD: Partial<Record<ItemName, readonly [number, number, boolean?]>> = {
  APPLE: [4, 2.4], BREAD: [5, 6], BEEF: [3, 1.8], COOKED_BEEF: [8, 12.8],
  PORKCHOP: [3, 1.8], COOKED_PORKCHOP: [8, 12.8], CHICKEN: [2, 1.2], COOKED_CHICKEN: [6, 7.2],
  MUTTON: [2, 1.2], COOKED_MUTTON: [6, 9.6], ROTTEN_FLESH: [4, 0.8], MELON_SLICE: [2, 1.2],
  COOKIE: [2, 0.4], GLOW_BERRIES: [2, 0.4], GOLDEN_APPLE: [4, 9.6, true], SPIDER_EYE: [2, 3.2],
  CARROT: [3, 3.6], POTATO: [1, 0.6], BAKED_POTATO: [5, 6], POISONOUS_POTATO: [2, 1.2], BEETROOT: [1, 1.2],
  GOLDEN_CARROT: [6, 14.4],
  PUFFERFISH: [1, 0.2], COD: [2, 0.4], SALMON: [2, 0.4], TROPICAL_FISH: [1, 0.2], COOKED_COD: [5, 6], COOKED_SALMON: [6, 9.6], ENCHANTED_GOLDEN_APPLE: [4, 9.6, true], SUSPICIOUS_STEW: [6, 7.2], HONEY_BOTTLE: [6, 1.2],
  RABBIT: [3, 1.8], COOKED_RABBIT: [5, 6], MUSHROOM_STEW: [6, 7.2],
};

// --- fuel (ticks) for items ----------------------------------------------------

const ITEM_FUEL: Partial<Record<ItemName, number>> = {
  COAL: 1600, CHARCOAL: 1600, STICK: 100,
  BLAZE_ROD: 2400,
  WOODEN_SPEAR: 200, WOODEN_SWORD: 200, WOODEN_PICKAXE: 200, WOODEN_AXE: 200, WOODEN_SHOVEL: 200, WOODEN_HOE: 200,
};

// --- names / sprites ---------------------------------------------------------

const NAME_OVERRIDE: Partial<Record<ItemName, string>> = {
  FLINT_AND_STEEL: 'Flint and Steel',
  BEEF: 'Raw Beef', PORKCHOP: 'Raw Porkchop', CHICKEN: 'Raw Chicken', MUTTON: 'Raw Mutton',
  GUNPOWDER: 'Gunpowder', LAPIS_LAZULI: 'Lapis Lazuli',
  QUARTZ: 'Nether Quartz',
  GLISTERING_MELON_SLICE: 'Glistering Melon Slice', TURTLE_SCUTE: 'Turtle Scute', DRAGON_BREATH: "Dragon's Breath", EYE_OF_ENDER: 'Eye of Ender',
};
const titleCase = (n: string): string =>
  n.toLowerCase().split('_').map((w) => w[0].toUpperCase() + w.slice(1)).join(' ');

/** Sprite basename under mc/item/. Every registry name maps to its lower-cased form
 *  (`GOLDEN_SWORD` -> `golden_sword`), which is the vanilla file name. */
export function spriteOf(id: number): string {
  const n = itemNameOf(id);
  if (n === 'CROSSBOW') return 'crossbow_standby';
  if (n === 'EYE_OF_ENDER') return 'ender_eye';
  if (n === 'HEAVY_CORE') return '../heavy_core'; // block texture (mc/heavy_core.png)
  if (n === 'ENCHANTED_GOLDEN_APPLE') return 'golden_apple'; // the glint is drawn over it
  return n ? n.toLowerCase() : '';
}

// --- registry ----------------------------------------------------------------

const ITEM_DEFS = new Map<number, ItemDef>();

function register(def: ItemDef): void { ITEM_DEFS.set(def.id, def); }

function base(name: ItemName, patch: Partial<ItemDef> = {}): ItemDef {
  const id = I[name];
  return {
    id, name: NAME_OVERRIDE[name] ?? titleCase(name), maxStack: 64, maxDamage: 0,
    fuel: ITEM_FUEL[name] ?? 0, ...patch,
  };
}

for (const id of ALL_ITEM_IDS) {
  const n = itemNameOf(id)!;
  const f = FOOD[n];
  register(base(n, f ? { food: { hunger: f[0], saturation: f[1], ...(f[2] ? { alwaysEdible: true } : {}) } } : {}));
}
// single-item stacks
for (const n of ['BUCKET', 'WATER_BUCKET', 'SADDLE'] as const) register(base(n, { maxStack: 1 }));
register(base('LAVA_BUCKET', { maxStack: 1, fuel: 20000 })); // 1000 s in a furnace, leaves an empty bucket (furnace.ts)
register(base('TOTEM_OF_UNDYING', { maxStack: 1 }));
register(base('EGG', { maxStack: 16 })); // vanilla: eggs (and snowballs) stack to 16
register(base('FLINT_AND_STEEL', { maxStack: 1, maxDamage: 64 }));
register(base('BOW', { maxStack: 1, maxDamage: 384 }));
register(base('SHIELD', { maxStack: 1, maxDamage: 336 }));
register(base('ENDER_PEARL', { maxStack: 16 }));
register(base('FIREWORK_ROCKET', {})); // Stack.fw = [duration, ...star colours]; game/fireworks.ts
register(base('FIREWORK_STAR', {})); // Stack.fw = [colour]
register(base('ELYTRA', { maxStack: 1, maxDamage: 432, armor: { slot: 1, defense: 0, toughness: 0 } })); // glides: game/elytra.ts
register(base('END_CRYSTAL')); // placed on obsidian / bedrock (main.ts)
register(base('CHORUS_FRUIT', { food: { hunger: 4, saturation: 2.4, alwaysEdible: true } })); // eaten -> random teleport (main.ts)
// vanilla 1.21: mace 6 dmg / 0.6 speed / 500 uses; trident 9 dmg / 1.1 speed / 250 uses
register(base('MACE', { maxStack: 1, maxDamage: 500, tool: { type: 'mace', tier: 0, speed: 1, attack: 6, attackSpeed: 0.6 } }));
register(base('TRIDENT', { maxStack: 1, maxDamage: 250, tool: { type: 'trident', tier: 0, speed: 1, attack: 9, attackSpeed: 1.1 } }));
register(base('ENCHANTED_BOOK', { maxStack: 1 }));
// potions & drinks: the potion type is Stack.dmg; vanilla bottles do not stack
for (const n of ['POTION', 'SPLASH_POTION', 'LINGERING_POTION', 'MILK_BUCKET'] as const) register(base(n, { maxStack: 1 }));
// the stew is food (6 hunger / 7.2 saturation) AND does not stack; registering it in the loop above dropped its food entry
register(base('SUSPICIOUS_STEW', { maxStack: 1, food: { hunger: 6, saturation: 7.2 } }));
register(base('MUSHROOM_STEW', { maxStack: 1, food: { hunger: 6, saturation: 7.2 } }));
register(base('SNOWBALL', { maxStack: 16 }));
register(base('GOAT_HORN', { maxStack: 1 }));
register(base('HONEY_BOTTLE', { maxStack: 16, food: { hunger: 6, saturation: 1.2 } }));
register(base('CROSSBOW', { maxStack: 1, maxDamage: 465 }));
register(base('FISHING_ROD', { maxStack: 1, maxDamage: 64 }));
// riding (game/mounts.ts): steering sticks (pig 25 uses, strider 100) and horse armour (loot only)
register(base('CARROT_ON_A_STICK', { maxStack: 1, maxDamage: 25 }));
register(base('WARPED_FUNGUS_ON_A_STICK', { maxStack: 1, maxDamage: 100 }));
for (const n of ['LEATHER_HORSE_ARMOR', 'IRON_HORSE_ARMOR', 'GOLDEN_HORSE_ARMOR', 'DIAMOND_HORSE_ARMOR'] as const) register(base(n, { maxStack: 1 }));
register(base('NAME_TAG'));
register(base('MINECART', { maxStack: 1 }));
for (const n of ['OAK', 'SPRUCE', 'BIRCH', 'JUNGLE', 'ACACIA', 'DARK_OAK', 'MANGROVE', 'CHERRY', 'PALE_OAK']) register(base(`${n}_BOAT` as ItemName, { maxStack: 1 }));
register(base('SHEARS', {
  maxStack: 1, maxDamage: 238,
  // cobweb / leaves / wool: the mining module treats shears as speed 15 on cobweb & leaves
  tool: { type: 'shears', tier: 0, speed: 15, attack: 1, attackSpeed: 4 },
}));
for (const m of MATERIALS) {
  const atk = ATTACK[m.key];
  const kinds: [string, ToolType, number, number][] = [
    ['SWORD', 'sword', atk[0], 1.6], ['AXE', 'axe', atk[1], AXE_SPEED[m.key]],
    ['PICKAXE', 'pickaxe', atk[2], 1.2], ['SHOVEL', 'shovel', atk[3], 1],
    ['HOE', 'hoe', atk[4], HOE_SPEED[m.key]],
  ];
  for (const [suffix, type, attack, attackSpeed] of kinds) {
    register(base(`${m.key}_${suffix}` as ItemName, {
      maxStack: 1, maxDamage: m.dur,
      tool: { type, tier: m.tier, speed: m.speed, attack, attackSpeed },
    }));
  }
}
// spears (game/spear.ts): tool.attack = jab damage, attackSpeed = jab speed; durability of the tier
for (const t of SPEAR_TIERS) {
  register(base(`${t.key}_SPEAR` as ItemName, {
    maxStack: 1, maxDamage: t.dur, tool: { type: 'spear', tier: t.tier, speed: 1, attack: t.damage, attackSpeed: t.speed },
    cooldownBar: true,
  }));
}
for (const m of ARMOR_MATS) {
  ARMOR_PIECES.forEach((p, slot) => {
    register(base(`${m.key}_${p}` as ItemName, {
      maxStack: 1, maxDamage: m.mult * ARMOR_BASE_DUR[slot],
      armor: {
        slot: slot as 0 | 1 | 2 | 3, defense: m.defense[slot], toughness: m.toughness,
        ...('kb' in m ? { knockbackResistance: m.kb } : {}),
      },
    }));
  });
}

// --- blocks ------------------------------------------------------------------

let blockFuel: Map<number, number> | null = null;
function blockFuelTable(): Map<number, number> {
  if (blockFuel) return blockFuel;
  const t = new Map<number, number>();
  const set = (ids: Iterable<number>, v: number): void => { for (const id of ids) t.set(id, v); };
  set([B.LOG_ID, B.SPRUCE_LOG_ID, B.BIRCH_LOG_ID, B.JUNGLE_LOG_ID, B.ACACIA_LOG_ID, B.DARK_OAK_LOG_ID,
    B.MANGROVE_LOG_ID, B.CHERRY_LOG_ID, B.PALE_OAK_LOG_ID,
    B.PLANKS_ID, B.SPRUCE_PLANKS_ID, B.BIRCH_PLANKS_ID, B.JUNGLE_PLANKS_ID, B.ACACIA_PLANKS_ID,
    B.DARK_OAK_PLANKS_ID, B.MANGROVE_PLANKS_ID, B.CHERRY_PLANKS_ID, B.PALE_OAK_PLANKS_ID,
    B.CRAFTING_TABLE_ID, B.BOOKSHELF_ID,
    ...B.CHEST_FACINGS, B.CHEST_OPEN_FACING_0, B.CHEST_OPEN_FACING_1, B.CHEST_OPEN_FACING_2, B.CHEST_OPEN_FACING_3,
    ...B.FALLEN_LOG_IDS.flat()], 300);
  set(B.DOOR_BOTTOM_IDS, 200);
  set(B.DOOR_TOP_IDS, 200);
  set(Object.values(B.WOOL_IDS), 100);
  set([B.OAK_SAPLING_ID, B.DEAD_BUSH_ID], 100);
  const coalBlock = BLOCKS.find((b) => b?.name === 'Block of Coal');
  if (coalBlock) t.set(coalBlock.id, 16000);
  // vanilla wooden-block fuel by name: slab 150, button 100, stairs / fence / gate / trapdoor / plate / sign / ladder
  // / barrel / lectern / composter 300, bamboo 50, block of dried kelp 4000 (20 items)
  const WOOD = '(?:Oak|Spruce|Birch|Jungle|Acacia|Dark Oak|Mangrove|Cherry|Pale Oak)';
  const byName: Array<[RegExp, number]> = [
    [new RegExp(`^${WOOD} Slab$`), 150], [new RegExp(`^${WOOD} Button$`), 100],
    [new RegExp(`^${WOOD} (?:Stairs|Fence|Fence Gate|Trapdoor|Pressure Plate|Sign|Wall Sign|Hanging Sign)$`), 300],
    [/^(?:Ladder|Barrel|Lectern|Composter|Jukebox|Note Block|Loom|Daylight Detector|Trapped Chest)$/, 300],
    [/^Bamboo$/, 50], [/^Dried Kelp Block$/, 4000],
  ];
  for (const b of BLOCKS) {
    if (!b || t.has(b.id)) continue;
    for (const [re, v] of byName) if (re.test(b.name) && v > 0) { t.set(b.id, v); break; }
  }
  blockFuel = t;
  return t;
}

function blockDef(id: number): ItemDef {
  const b = BLOCKS[id];
  return { id, name: b?.name ?? '', maxStack: 64, maxDamage: 0, fuel: blockFuelTable().get(id) ?? 0 };
}

const UNKNOWN: ItemDef = { id: 0, name: '', maxStack: 64, maxDamage: 0, fuel: 0 };

export function itemDef(id: number): ItemDef {
  if (id >= ITEM_BASE) return ITEM_DEFS.get(id) ?? { ...UNKNOWN, id };
  let d = BLOCK_DEFS.get(id);
  if (!d) { d = id > 0 ? blockDef(id) : { ...UNKNOWN, id }; BLOCK_DEFS.set(id, d); }
  return d;
}
const BLOCK_DEFS = new Map<number, ItemDef>();

export const maxStackOf = (id: number): number => itemDef(id).maxStack;
export const maxDamageOf = (id: number): number => itemDef(id).maxDamage;
